import { inject } from "@angular/core";
import { auditTime, debounceTime, map, withLatestFrom } from "rxjs";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { cameraActions } from "./camera.actions";
import { Vector3 } from "three";
import { Store } from "@ngrx/store";
import { ThreeService } from "src/app/binst/rendering/threejs/three.service";

import { animate, utils, engine } from 'animejs';
import { selectCameraTarget, selectHistory } from "./camera.selector";
import { worldActions } from "../world/world.actions";
import { EventsWebsocketService } from "../../services/entities/events-websocket.service";
import { selectUserAvatarId } from "../auth/auth.selector";
import { LookingAtProperties } from "../entities/component-properties-dto/looking-at.properties.dto";
import { entityActions } from "../entities/entity.actions";
import { EntityType, Components, CollisionType } from "../entities/entity.model";
import {settings} from "../../settings/settings";

engine.defaults.frameRate = settings.framerate;
engine.precision = 3


let resizeInterval: any;
let resizeIntervalTimeLeft = 0;
export const triggeredCanvasResizeAnimation = createEffect(
  (actions$ = inject(Actions), threeService = inject(ThreeService)) => {
    return actions$.pipe(
      ofType(cameraActions.triggeredCanvasResizeAnimation),
      map((action) => {
        resizeIntervalTimeLeft = action.durationMs;
        resizeInterval = setInterval(() => {
          resizeIntervalTimeLeft -= 15;
          if (resizeIntervalTimeLeft <= 0) {
            clearInterval(resizeInterval);
          }
          threeService.resize();
        }, 15);
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const rewindTarget = createEffect(
  (actions$ = inject(Actions), store = inject(Store), threeService = inject(ThreeService)) => {
    return actions$.pipe(
      ofType(cameraActions.rewindTarget),
      withLatestFrom(store.select(selectHistory)),
      map(([action, history]) => {
        if (history.length == 0) return
        store.dispatch(cameraActions.targetJumped({
          target: history[history.length - 1], jumpTimeMs: 1000, skipCheckpoint: true
        }))
        store.dispatch(cameraActions.popCheckpointHistory())
      }),
    );
  },
  { functional: true, dispatch: false },
);

let rewindTrackLastCameraPos = new Vector3()
export const addTimedCheckpoint = createEffect(
  (
    actions$ = inject(Actions),
    threeService = inject(ThreeService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(worldActions.timerTicked10s),
      map((action) => {
        if (rewindTrackLastCameraPos.distanceTo(threeService.camera.position) > 10) {
          store.dispatch(cameraActions.addRewindCheckpointTarget({
            target: threeService.camera.position.clone()
          }))
        }
        rewindTrackLastCameraPos.copy(threeService.camera.position)
      }),
    );
  },
  { functional: true, dispatch: false },
);


export const cameraMoved = createEffect(
  (
    actions$ = inject(Actions),
    websocketService = inject(EventsWebsocketService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(cameraActions.targetMoved, cameraActions.zoomChanged),
      withLatestFrom(store.select(selectUserAvatarId), store.select(selectCameraTarget)),
      auditTime(100),
      map(([action, userAvatarId, cameraTarget]) => {
        let newTarget = new Vector3(cameraTarget.x, cameraTarget.y, cameraTarget.z)
        if (action.type === cameraActions.zoomChanged.type) {
          newTarget.z = action.absoluteZoom
        }
        if (action.type === cameraActions.targetMoved.type) {
          newTarget = action.target.clone()
        }
        websocketService.dispatch(
          entityActions.netMoved({
            entityId: userAvatarId,
            oldPos: new Vector3(),
            newPos: new Vector3(newTarget.x, newTarget.y, newTarget.z)
            // newPos: new Vector3(newTarget.x, newTarget.y, newTarget.z - 701)
          }),
        );
      })
    );
  },
  { functional: true, dispatch: false },
);

let jumpCameraTrack = new Vector3()
export const cameraJumped = createEffect(
  (
    actions$ = inject(Actions),
    threeService = inject(ThreeService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(cameraActions.targetJumped),
      map((action) => {
        let jumpTime = action.jumpTimeMs ?? 1000
          if (!action.target) {
              return
          }
        if (action.target.z) {
          store.dispatch(
            cameraActions.zoomJumped({ absoluteZoom: action.target.z }),
          );
        }
        jumpCameraTrack.copy(threeService.camera.position)
        if (!action.skipCheckpoint) {
          store.dispatch(cameraActions.addRewindCheckpointTarget({
            target: threeService.camera.position.clone()
          }))
        }
        animate(jumpCameraTrack, {
          x: action.target.x,
          y: action.target.y,
          duration: jumpTime,
          ease: 'out(5)',
          onUpdate: () => store.dispatch(
            cameraActions.targetMoved({
              target: new Vector3(
                jumpCameraTrack.x,
                jumpCameraTrack.y,
                0,
              ),
            }),
          )
        });
      }),
    );
  },
  { functional: true, dispatch: false },
);

let jumpZoomTrack = {z: 0}
export const cameraZoomJumped = createEffect(
  (
    actions$ = inject(Actions),
    threeService = inject(ThreeService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(cameraActions.zoomJumped),
      map((action) => {
        let jumpTime = action.jumpTimeMs ?? 1000
        jumpZoomTrack.z = threeService.__camera.position.z
        animate(jumpZoomTrack, {
          z: action.absoluteZoom,
          duration: jumpTime,
          ease: 'out(5)',
          onUpdate: () => store.dispatch(
            cameraActions.zoomChanged({
              absoluteZoom: jumpZoomTrack.z,
            }),
          )
        });
      }),
    );
  },
  { functional: true, dispatch: false },
);

